import React from 'react';
import { FiAlertTriangle, FiInfo } from 'react-icons/fi';

/**
 * Reusable Confirmation Modal
 * 
 * @param {boolean} isOpen - Whether the modal is visible
 * @param {function} onClose - Function to call when closing/cancelling
 * @param {function} onConfirm - Function to call when confirming
 * @param {string} title - Modal title
 * @param {string} message - Main confirmation message
 * @param {string} subMessage - detailed warning or sub-text
 * @param {string} type - 'danger' | 'warning' | 'info'
 * @param {string} confirmText - Text for the confirm button
 * @param {boolean} isLoading - Loading state for the confirm action
 */
const ConfirmationModal = ({ 
  isOpen, 
  onClose, 
  onConfirm, 
  title, 
  message, 
  subMessage,
  type = 'warning',
  confirmText = 'Confirm',
  isLoading = false
}) => {
  if (!isOpen) return null;

  const getTypeStyles = () => {
    switch (type) {
      case 'danger':
        return {
          icon: <FiAlertTriangle className="text-[var(--badge-loss-fg)] text-3xl" />,
          button: 'btn-danger',
          bgIcon: 'bg-[var(--badge-loss-bg)]'
        };
      case 'info':
        return {
          icon: <FiInfo className="text-[var(--badge-info-fg)] text-3xl" />,
          button: 'btn-primary',
          bgIcon: 'bg-[var(--badge-info-bg)]'
        };
      default: // warning
        return {
          icon: <FiAlertTriangle className="text-[var(--badge-warn-fg)] text-3xl" />,
          button: 'btn-danger',
          bgIcon: 'bg-[var(--badge-warn-bg)]'
        };
    }
  };

  const styles = getTypeStyles();

  return (
    <div className="fixed inset-0 z-50 overflow-y-auto" aria-labelledby="modal-title" role="dialog" aria-modal="true">
      <div className="flex items-end justify-center min-h-screen pt-4 px-4 pb-20 text-center sm:block sm:p-0">
        
        {/* Backdrop */}
        <div 
          className="fixed inset-0 bg-black/50 transition-opacity"
          aria-hidden="true"
          onClick={onClose}
        ></div>

        <span className="hidden sm:inline-block sm:align-middle sm:h-screen" aria-hidden="true">&#8203;</span>

        {/* Modal Panel */}
        <div className="inline-block align-bottom bg-bg-2 rounded-lg text-left overflow-hidden shadow-xl transform transition-all sm:my-8 sm:align-middle sm:max-w-lg w-full">
          <div className="bg-bg-2 px-4 pt-5 pb-4 sm:p-6 sm:pb-4">
            <div className="sm:flex sm:items-start">
              <div className={`mx-auto flex-shrink-0 flex items-center justify-center h-12 w-12 rounded-full ${styles.bgIcon} sm:mx-0 sm:h-10 sm:w-10`}>
                {styles.icon}
              </div>
              <div className="mt-3 text-center sm:mt-0 sm:ml-4 sm:text-left">
                <h3 className="ds-h3 leading-6 text-fg-1" id="modal-title">
                  {title}
                </h3>
                <div className="mt-2">
                  <p className="text-sm text-fg-2">
                    {message}
                  </p>
                  {subMessage && (
                    <p className="mt-2 text-xs text-[var(--badge-loss-fg)] bg-[var(--badge-loss-bg)] p-2 rounded border border-loss/30">
                      {subMessage}
                    </p>
                  )}
                </div>
              </div>
            </div>
          </div>
          <div className="bg-bg-3 px-4 py-3 sm:px-6 sm:flex sm:flex-row-reverse gap-2">
            <button
              type="button"
              className={`btn ${styles.button} w-full justify-center sm:ml-3 sm:w-auto ${isLoading ? 'opacity-75 cursor-wait' : ''}`}
              onClick={onConfirm}
              disabled={isLoading}
            >
              {isLoading ? 'Processing...' : confirmText}
            </button>
            <button
              type="button"
              className="btn btn-secondary mt-3 w-full justify-center sm:mt-0 sm:ml-3 sm:w-auto"
              onClick={onClose}
              disabled={isLoading}
            >
              Cancel
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ConfirmationModal;
